import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Bell, Check, Database, QrCode, Smartphone } from 'lucide-react';
import { Button } from '../../../components/Button';
import { InfoItem } from '../../../components/common/InfoItem';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { usePrototype } from '../../../contexts/PrototypeContext';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { queueOf } from '../../../utils/applicationQueue';
export function ApprovalSuccess() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    applications
  } = useOwnerService();
  const {
    getCng
  } = usePrototype();
  const {
    getReview
  } = useApplicationReview();
  const app = applications.find(a => a.id === id);
  if (!app || queueOf(app) !== 'approved') return <Navigate to={`/admin/owner/applications/${id ?? ''}`} replace data-id="element-3300" data-mp="3fxir89deckpw" />;
  const r = getReview(app);
  const cng = app.vehicleId ? getCng(app.vehicleId) : undefined;
  const stickerDone = (cng?.stage ?? 0) >= 5;
  const effects = [{
    icon: Check,
    text: 'আবেদনের অবস্থা “অনুমোদিত” করা হয়েছে'
  }, {
    icon: Database,
    text: `DMP যানবাহন ডাটাবেজে রেকর্ড ${cng ? `যুক্ত/হালনাগাদ (${cng.qrId})` : 'হালনাগাদ'} হয়েছে`
  }, {
    icon: Smartphone,
    text: 'মালিকের মোবাইল অ্যাপে অবস্থা হালনাগাদ হয়েছে'
  }, {
    icon: Bell,
    text: 'মালিককে অনুমোদনের নোটিফিকেশন ও SMS পাঠানো হয়েছে'
  }];
  return <div className="mx-auto max-w-3xl" data-id="element-3301" data-mp="3puwtn52hj8z7">
      <Link to="/admin/owner/applications" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-navy-700 hover:underline" data-id="element-3302" data-mp="3ptvh0w5tmefd"><ArrowLeft className="h-4 w-4" data-id="element-3303" data-mp="m6d1lnhyuusg" />আবেদন তালিকা</Link>
      <section className="overflow-hidden rounded-lg border border-border bg-white shadow-sm" data-id="element-3304" data-mp="m7xyhjrr6pth">
        <div className="flex flex-col items-center border-b border-border bg-emerald-50/60 px-6 py-8 text-center" data-id="element-3305" data-mp="hev459f5jcc8">
          <motion.span initial={{
          scale: 0.6,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 18
        }} className="flex h-20 w-20 items-center justify-center rounded-full bg-bdgreen-600 text-white shadow-lg" data-id="element-3306" data-mp="2boa8ijgx1jh7">
            <Check className="h-11 w-11" strokeWidth={3} data-id="element-3307" data-mp="26vz2cyvl11g7" />
          </motion.span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-navy-900" data-id="element-3308" data-mp="1ryzwbfgdpgnu">আবেদন অনুমোদিত হয়েছে</h1>
          <p className="mt-1 text-sm text-slate-600" data-id="element-3309" data-mp="1wz43ufzb711x">গাড়িটি এখন QR স্টিকার প্রক্রিয়ার জন্য যোগ্য</p>
        </div>
        <dl className="grid gap-x-6 gap-y-4 px-6 py-5 sm:grid-cols-3" data-id="element-3310" data-mp="1izuykbcn5rcq">
          <InfoItem label="Application ID" value={app.id} mono data-id="element-3311" data-mp="1nxmncwhzoxcp" />
          <InfoItem label="গাড়ির রেজিস্ট্রেশন" value={app.vehicleReg} data-id="element-3312" data-mp="1o00h920xo3io" />
          <InfoItem label="মালিকের নাম" value={app.ownerName} data-id="element-3313" data-mp="3de5l2d5rk9fc" />
          <InfoItem label="অনুমোদনের তারিখ" value={r.approval?.date ?? '১৩/১০/২০২৬'} data-id="element-3314" data-mp="3icgwutfmcu07" />
          <InfoItem label="অনুমোদনকারী" value={r.approval ? `${r.approval.officer}, ${r.approval.designation}` : '—'} data-id="element-3315" data-mp="3sfvqcm6mu26z" />
          <InfoItem label="পরবর্তী ধাপ" value={<StatusBadge tone="blue" data-id="element-3317" data-mp="3nicaxiy77jdg">{stickerDone ? 'QR স্টিকার ইতোমধ্যে তৈরি/ইস্যুকৃত' : 'QR স্টিকার তৈরির জন্য প্রস্তুত'}</StatusBadge>} data-id="element-3316" data-mp="3nhavl2a5gapx" />
        </dl>
        <ul className="grid gap-2 border-t border-border px-6 py-4 sm:grid-cols-2" data-id="element-3318" data-mp="cbhf3wmph33">
          {effects.map(e => <li key={e.text} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-3319" data-mp="f06awmignph8"><e.icon className="h-4 w-4 shrink-0 text-emerald-600" data-id="element-3320" data-mp="a24u688ic28x" />{e.text}</li>)}
        </ul>
        <div className="flex flex-wrap justify-end gap-2 border-t border-border bg-slate-50 px-6 py-4" data-id="element-3321" data-mp="o25w7r0idfpz">
          <Button variant="outline" onClick={() => navigate('/admin/owner/applications')} data-id="element-3322" data-mp="jzj21jgd7ipm">আবেদন তালিকায় ফিরে যান</Button>
          <Button onClick={() => navigate(`/admin/qr/${app.vehicleId ?? ''}`)} disabled={!app.vehicleId} data-id="element-3323" data-mp="2pu28x3yewvmc"><QrCode className="h-4 w-4" data-id="element-3324" data-mp="2lsgwfjpsijco" />QR স্টিকার ব্যবস্থাপনায় যান</Button>
        </div>
      </section>
      <p className="mt-3 text-center text-xs text-slate-500" data-id="element-3325" data-mp="2n7t2vdvdflkp">অফিসিয়াল QR স্টিকার কেবল অনুমোদিত গাড়ির জন্য তৈরি হয় — অপেক্ষমাণ, বাতিল বা অযাচাইকৃত গাড়ির জন্য নয়।</p>
    </div>;
}